import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateRecordMutation, useGetRecordTypeQuery } from '@platform/state/api';
import type { IApiErrorPlain } from '@platform/core/types';
import { Button, Dialog, ErrorBanner } from '@platform/ui';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { FieldRenderer } from '../FieldRenderer';
import { inlineValue } from '../inlineValue';
import type { IQuickCreateDialogProps } from './IQuickCreateDialogProps';

/**
 * Швидке створення з поля-посилання (UIUX/forms/form-variants, Lookup «Quick create with required fields»): вікно 560px з назвою
 * (введений текст) і обов'язковими полями цільового типу; «Створити» додає запис і підставляє його в поле.
 */
export function QuickCreateDialog({ ws, target, title, onCreated, onClose }: IQuickCreateDialogProps) {
  const { t } = useTranslation();
  const { data: meta } = useGetRecordTypeQuery(target);
  const strings = useModuleStrings(meta?.module.module.key);
  const [values, setValues] = useState<Record<string, unknown>>({ title });
  const [create, { isLoading, error }] = useCreateRecordMutation();
  const rt = meta?.recordType;
  const fields = (rt?.fields ?? []).filter((f) => f.required && f.key !== 'title' && f.key !== 'status');
  const errors = (error as IApiErrorPlain | undefined)?.errors ?? {};
  const submit = async () => {
    const rec = await create({ ws, rt: target, body: { values } }).unwrap().catch(() => null);
    if (rec) onCreated(rec);
  };
  const singular = rt ? strings.entity(target, 'singular', rt.singular ?? rt.name) : target;
  return (
    <Dialog title={t('fieldControls.quickCreate', { type: singular })} onClose={onClose}
      footer={<><Button onClick={onClose}>{t('app.cancel')}</Button><Button variant="primary" disabled={isLoading || !rt} onClick={() => void submit()}>{t('app.create')}</Button></>}>
      <div className="field">
        <label className="field__label" htmlFor="qc-title">{t('fieldControls.colTitle')}<span className="req">*</span></label>
        <input id="qc-title" className="control control--title" autoFocus value={String(values['title'] ?? '')}
          onChange={(e) => setValues((v) => ({ ...v, title: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }} />
        {errors['title'] && <span className="field__error">{errors['title'].join('; ')}</span>}
      </div>
      {rt && fields.length > 0 && (
        <div className="group__grid">
          {fields.map((f) => <FieldRenderer key={f.key} field={f} rt={rt} ws={ws} value={values[f.key]} error={errors[f.key]?.join('; ')} idPrefix="qc"
            onChange={(k, v) => setValues((s) => ({ ...s, [k]: inlineValue(v) }))} />)}
        </div>
      )}
      <ErrorBanner error={error && !Object.keys(errors).length ? (error as IApiErrorPlain) : undefined} />
    </Dialog>
  );
}
